import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HardDrive, Plus, Server, Cloud, FolderTree, CheckCircle2, AlertTriangle, CircleDot,
  Building2, Boxes,
} from 'lucide-react';
import { sicherungsziele as api, customers } from '../api';
import type { SicherungsZiel, SicherungsZielArt } from '../api';

/**
 * Wohin Sicherungen geschrieben werden.
 *
 * ══ WARUM DAS EINE EIGENE SEITE IST ════════════════════════════════════════
 *
 * Ein Ziel gehört keiner Instanz. Es wird von mehreren geteilt: ein Kunde
 * bekommt einen Speicherplatz, alle seine Instanzen schreiben dorthin — und
 * wenn sich das Passwort ändert, ändert es sich an einer Stelle statt an
 * zwölf. An einer Instanz hinge es falsch.
 *
 * ══ WAS HIER NIE STEHT ═════════════════════════════════════════════════════
 *
 * Das Geheimnis. Es geht verschlüsselt hinein und kommt nie zurück; die Liste
 * sagt nur, ob eines hinterlegt ist. Ein leeres Feld beim Bearbeiten heißt
 * deshalb „unverändert" und nicht „löschen" — sonst würde jeder, der den Namen
 * korrigiert, nebenbei den Zugang entfernen.
 */

const ARTEN: Array<{
  wert: SicherungsZielArt;
  name: string;
  erklaerung: string;
  braucht: string[];
}> = [
  {
    wert: 'LOKAL',
    name: 'Auf dem Wirt',
    erklaerung:
      'Die Sicherung bleibt dort, wo sie entsteht. Sofort da und ohne Zugangsdaten — '
      + 'aber kein vollwertiges Backup: geht der Wirt, geht sie mit.',
    braucht: [],
  },
  {
    wert: 'SFTP',
    name: 'SFTP',
    erklaerung: 'Über SSH. Als Geheimnis gehört hier ein privater Schlüssel hinein, kein Passwort.',
    braucht: ['host', 'benutzer', 'geheimnis'],
  },
  {
    wert: 'FTP',
    name: 'FTP / FTPS',
    erklaerung: 'Klassisches FTP. Verschlüsselt, sofern der Server FTPS kann.',
    braucht: ['host', 'benutzer', 'geheimnis'],
  },
  {
    wert: 'S3',
    name: 'S3 & kompatible',
    erklaerung:
      'Amazon S3, Backblaze B2, MinIO, Wasabi. Benutzer ist der Access Key, '
      + 'das Geheimnis der Secret Key. Bucket und Endpunkt stehen in den Optionen.',
    braucht: ['benutzer', 'geheimnis', 'bucket'],
  },
  {
    wert: 'WEBDAV',
    name: 'WebDAV / Nextcloud',
    erklaerung:
      'Deckt Nextcloud und ownCloud ab, auch selbst betriebene. Bei Nextcloud gehört '
      + 'hier ein App-Passwort hinein — dann kostet ein Wechsel nicht den Zugang zum Konto.',
    braucht: ['host', 'benutzer', 'geheimnis'],
  },
];

function ArtZeichen({ art }: { art: SicherungsZielArt }) {
  if (art === 'LOKAL') return <HardDrive size={15} className="text-slate-500" />;
  if (art === 'S3') return <Cloud size={15} className="text-slate-500" />;
  if (art === 'WEBDAV') return <FolderTree size={15} className="text-slate-500" />;
  return <Server size={15} className="text-slate-500" />;
}

function ZielFormular({
  ziel,
  onFertig,
}: {
  ziel: SicherungsZiel | null;
  onFertig: () => void;
}) {
  const qc = useQueryClient();
  const [art, setArt] = useState<SicherungsZielArt>(ziel?.art ?? 'SFTP');
  const [name, setName] = useState(ziel?.name ?? '');
  const [host, setHost] = useState(ziel?.host ?? '');
  const [port, setPort] = useState(ziel?.port ? String(ziel.port) : '');
  const [benutzer, setBenutzer] = useState(ziel?.benutzer ?? '');
  const [geheimnis, setGeheimnis] = useState('');
  const [pfad, setPfad] = useState(ziel?.pfad ?? '/{instanz}/sicherungen');
  const [bucket, setBucket] = useState(String(ziel?.optionen?.bucket ?? ''));
  const [endpoint, setEndpoint] = useState(String(ziel?.optionen?.endpoint ?? ''));
  const [region, setRegion] = useState(String(ziel?.optionen?.region ?? ''));
  const [customerId, setCustomerId] = useState(ziel?.customerId ?? '');
  const [fehler, setFehler] = useState('');

  // Fuer die Zuordnung. Ein Ziel ohne Kunden ist unseres und steht allen
  // Instanzen offen — das ist der haeufigere Fall und deshalb die Vorgabe.
  const { data: kunden } = useQuery({
    queryKey: ['customers', 'fuer-ziele'],
    queryFn: () => customers.list({ pageSize: 200 }),
  });

  const beschreibung = ARTEN.find((a) => a.wert === art);
  const braucht = (feld: string) => beschreibung?.braucht.includes(feld) ?? false;

  const speichern = useMutation({
    mutationFn: () => {
      const daten = {
        name: name.trim(),
        art,
        pfad: pfad.trim() || undefined,
        customerId: customerId || null,
        host: host.trim() || null,
        port: port.trim() ? Number(port) : null,
        benutzer: benutzer.trim() || null,
        // Nur mitschicken, wenn etwas getippt wurde — leer heißt „unverändert".
        ...(geheimnis ? { geheimnis } : {}),
        optionen: art === 'S3'
          ? { bucket: bucket.trim(), endpoint: endpoint.trim(), region: region.trim() }
          : {},
      };
      return ziel ? api.aendern(ziel.id, daten) : api.anlegen(daten);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sicherungsziele'] });
      onFertig();
    },
    onError: (e: unknown) =>
      setFehler(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Das Ziel ließ sich nicht speichern.',
      ),
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-5">
      <h3 className="text-sm font-semibold text-slate-800">
        {ziel ? `„${ziel.name}" bearbeiten` : 'Neues Ziel'}
      </h3>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="block text-[11px] font-medium text-slate-600">Name</span>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Hetzner Storage Box"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>

        <label className="block">
          <span className="block text-[11px] font-medium text-slate-600">Art</span>
          <select
            value={art} onChange={(e) => setArt(e.target.value as SicherungsZielArt)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            {ARTEN.map((a) => (
              <option key={a.wert} value={a.wert}>{a.name}</option>
            ))}
          </select>
        </label>
      </div>

      {beschreibung && (
        <p className="mt-2 text-[11px] leading-snug text-slate-500">{beschreibung.erklaerung}</p>
      )}

      <label className="mt-4 block">
        <span className="block text-[11px] font-medium text-slate-600">Gehört zu</span>
        <select
          value={customerId} onChange={(e) => setCustomerId(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:w-1/2"
        >
          <option value="">uns (für alle Instanzen nutzbar)</option>
          {(kunden ?? []).map((k) => (
            <option key={k.id} value={k.id}>{k.companyName}</option>
          ))}
        </select>
        <span className="mt-1 block text-[11px] leading-snug text-slate-500">
          Stellt ein Kunde den Speicher selbst, gehört das Ziel ihm. Dann bieten nur seine
          Instanzen es an — damit die Sicherung eines Verlags nicht im Speicher eines
          anderen landet.
        </span>
      </label>

      {art !== 'LOKAL' && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {braucht('host') && (
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-600">
                {art === 'WEBDAV' ? 'Basis-Adresse' : 'Host'}
              </span>
              <input
                value={host} onChange={(e) => setHost(e.target.value)}
                placeholder={art === 'WEBDAV' ? 'https://cloud.example.de/remote.php/dav/files/juni' : 'u12345.your-storagebox.de'}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
              />
            </label>
          )}

          {braucht('host') && art !== 'WEBDAV' && (
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-600">Port</span>
              <input
                type="number" value={port} onChange={(e) => setPort(e.target.value)}
                placeholder={art === 'SFTP' ? '22' : '21'}
                className="mt-1 w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm tabular-nums"
              />
            </label>
          )}

          <label className="block">
            <span className="block text-[11px] font-medium text-slate-600">
              {art === 'S3' ? 'Access Key' : 'Benutzer'}
            </span>
            <input
              value={benutzer} onChange={(e) => setBenutzer(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
            />
          </label>

          <label className="block">
            <span className="block text-[11px] font-medium text-slate-600">
              {art === 'S3' ? 'Secret Key' : art === 'SFTP' ? 'Privater Schlüssel' : 'Passwort'}
            </span>
            {art === 'SFTP' ? (
              <textarea
                rows={3} value={geheimnis} onChange={(e) => setGeheimnis(e.target.value)}
                placeholder={ziel?.hatGeheimnis ? '(hinterlegt — leer lassen heißt unverändert)' : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-[11px]"
              />
            ) : (
              <input
                type="password" value={geheimnis} onChange={(e) => setGeheimnis(e.target.value)}
                placeholder={ziel?.hatGeheimnis ? '(hinterlegt — leer lassen heißt unverändert)' : ''}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            )}
          </label>

          {art === 'S3' && (
            <>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-600">Bucket</span>
                <input
                  value={bucket} onChange={(e) => setBucket(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-600">
                  Endpunkt <span className="font-normal text-slate-400">(leer = Amazon)</span>
                </span>
                <input
                  value={endpoint} onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="s3.eu-central-003.backblazeb2.com"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-medium text-slate-600">Region</span>
                <input
                  value={region} onChange={(e) => setRegion(e.target.value)}
                  placeholder="eu-central-1"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
                />
              </label>
            </>
          )}
        </div>
      )}

      <label className="mt-4 block">
        <span className="block text-[11px] font-medium text-slate-600">Verzeichnis am Ziel</span>
        <input
          value={pfad} onChange={(e) => setPfad(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
        />
        <span className="mt-1 block text-[11px] text-slate-500">
          <code className="font-mono">{'{instanz}'}</code> wird durch den Stacknamen ersetzt —
          so schreibt jede Instanz in ihren eigenen Ordner statt alle in denselben.
        </span>
      </label>

      {fehler && <p className="mt-3 text-xs text-red-600">{fehler}</p>}

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={() => speichern.mutate()}
          disabled={!name.trim() || speichern.isPending}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {speichern.isPending ? 'Speichert…' : 'Speichern'}
        </button>
        <button onClick={onFertig} className="text-xs text-slate-500 hover:text-slate-700">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

export default function SicherungszielePage() {
  const [formular, setFormular] = useState<{ ziel: SicherungsZiel | null } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sicherungsziele'],
    queryFn: () => api.list(),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Sicherungsziele</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Wohin Datensicherungen geschrieben werden. Ein Ziel wird von mehreren Instanzen
            geteilt — deshalb steht es hier und nicht an einer einzelnen.
          </p>
        </div>
        {!formular && (
          <button
            onClick={() => setFormular({ ziel: null })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white"
          >
            <Plus size={13} /> Ziel hinzufügen
          </button>
        )}
      </div>

      {formular && (
        <ZielFormular ziel={formular.ziel} onFertig={() => setFormular(null)} />
      )}

      {isLoading ? (
        <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
      ) : !data?.length ? (
        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-xs text-slate-500">
          Noch kein Ziel. Ohne eines bleiben alle Sicherungen auf dem Wirt, auf dem sie
          entstehen — das ist schnell verfügbar, aber kein Schutz gegen den Ausfall
          dieses Wirts.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {data.map((z) => (
            <div key={z.id} className="rounded-xl border border-slate-200 bg-surface p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                  <ArtZeichen art={z.art} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">{z.name}</p>
                    <p className="truncate text-[11px] text-slate-500">
                      {ARTEN.find((a) => a.wert === z.art)?.name}
                      {z.host && <span className="font-mono"> · {z.host}</span>}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-500">
                      <Building2 size={10} className="shrink-0 text-slate-400" />
                      {z.customer ? z.customer.companyName : 'unseres'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setFormular({ ziel: z })}
                  className="shrink-0 text-xs text-brand-600 hover:text-brand-800"
                >
                  Bearbeiten
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2 text-[11px]">
                {!z.aktiv && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500">
                    abgeschaltet
                  </span>
                )}
                {z.art !== 'LOKAL' && (
                  <span className={z.hatGeheimnis ? 'text-slate-500' : 'text-amber-700'}>
                    {z.hatGeheimnis ? 'Zugangsdaten hinterlegt' : 'ohne Zugangsdaten'}
                  </span>
                )}
                {/* Der letzte Versuch — ein Ziel, das seit Wochen nicht
                    erreichbar ist, soll man sehen, ohne auf die nächste
                    Sicherung zu warten. */}
                {z.zuletztGeprueft ? (
                  <span className="inline-flex items-center gap-1">
                    {z.zuletztOk
                      ? <CheckCircle2 size={11} className="text-emerald-600" />
                      : <AlertTriangle size={11} className="text-red-600" />}
                    <span className={z.zuletztOk ? 'text-slate-500' : 'text-red-600'}>
                      {z.zuletztOk ? 'zuletzt erfolgreich' : 'zuletzt fehlgeschlagen'}
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-slate-400">
                    <CircleDot size={11} /> noch nicht benutzt
                  </span>
                )}
              </div>

              {z.letzterFehler && !z.zuletztOk && (
                <p className="mt-1 text-[11px] leading-snug text-red-600">{z.letzterFehler}</p>
              )}

              {/* Wer darauf schreibt. Ohne das ist nicht zu beurteilen, ob man
                  das Ziel abschalten oder ändern kann — und die Frage kommt
                  bei jeder Änderung neu auf. */}
              <div className="mt-2 flex items-start gap-1.5 border-t border-slate-100 pt-2">
                <Boxes size={11} className="mt-0.5 shrink-0 text-slate-400" />
                {z.verwendetVon.length === 0 ? (
                  <span className="text-[11px] text-slate-400">
                    Von keiner Instanz verwendet.
                  </span>
                ) : (
                  <span className="text-[11px] text-slate-600">
                    {z.verwendetVon.length === 1 ? 'Verwendet von ' : `Verwendet von ${z.verwendetVon.length}: `}
                    {z.verwendetVon.slice(0, 4).map((v, i) => (
                      <span key={v.id}>
                        {i > 0 && ', '}
                        <Link
                          to={`/instances/${v.id}?reiter=sicherungen`}
                          className="font-mono text-brand-700 hover:underline"
                          title={v.ueber}
                        >
                          {v.stackName ?? v.id.slice(0, 8)}
                        </Link>
                      </span>
                    ))}
                    {z.verwendetVon.length > 4 && ` und ${z.verwendetVon.length - 4} weitere`}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
